import { useEffectWithController } from '@carvana/merch-react-hooks';
import equals from 'ramda/es/equals';
import { useContext } from 'react';
import useGetClientId from '~/constants/srp/getClientId';
import ErrorContext from '~/context/srp/ErrorContext';
import InventoryContext from '~/context/srp/InventoryContext';
import { useUserId, useUserLocation } from '~/context/srp/UserContext';
import type { Vehicle } from '~/contracts/srp';
import useMemoCompare from '~/hooks/srp/useMemoCompare';
import fetchAvailability from '~/services/fetchAvailability';
import generateErrorLogger, { logError } from '~/utilities/srp/errorHandlers';
import { addAvailabilityToVehicles } from './helpers';

const useGetAvailability = () => {
  const {
    setSimilarVehicles,
    similarVehicles: { vehicles, searchRequestId }
  } = useContext(InventoryContext);
  const { updateResponseErrors } = useContext(ErrorContext);
  const { zip } = useUserLocation();
  const userId = useUserId();
  const clientId = useGetClientId();

  const stableVehicles = useMemoCompare<Vehicle[]>(vehicles, (prev, next) =>
    equals(
      prev.map(vehicle => vehicle.vehicleId),
      next.map(vehicle => vehicle.vehicleId)
    )
  );

  const shouldCallAvailability = stableVehicles.length > 0;

  useEffectWithController(
    controller => {
      if (!shouldCallAvailability) return;

      fetchAvailability({
        clientId,
        zip,
        userId,
        vehicles: stableVehicles,
        searchRequestId,
        controller
      })
        .then(res => {
          if (res.data.status === 'Succeeded') {
            const updatedVehicles = addAvailabilityToVehicles(
              stableVehicles,
              res.data.content ?? []
            );

            setSimilarVehicles({ vehicles: updatedVehicles, searchRequestId });

            if (updatedVehicles.some(v => v.pickupInfo || v.deliveryInfo)) {
              updateResponseErrors({ lowAvailabilityError: null });
            }
          } else {
            logError(new Error(res.data.errorMessage), {
              location: 'Low Inventory Availability'
            });

            const vehicle = res.data.content?.find(
              v =>
                v.soonestDelivery?.errorMessage || v.soonestPickup?.errorMessage
            );

            const errorMessage =
              vehicle?.soonestDelivery?.errorMessage ||
              vehicle?.soonestPickup?.errorMessage;

            updateResponseErrors({ lowAvailabilityError: errorMessage });
          }
        })
        .catch(
          generateErrorLogger(
            'Availability',
            {},
            {
              callback: () => {
                updateResponseErrors({
                  lowAvailabilityError:
                    'Failed to fetch availability for low inventory.'
                });
              },
              controller
            }
          )
        );
    },
    [
      clientId,
      searchRequestId,
      shouldCallAvailability,
      setSimilarVehicles,
      stableVehicles,
      updateResponseErrors,
      userId,
      zip
    ]
  );
};

export default useGetAvailability;
